import { buildTerrain, type LevelDef, type LevelStyle, type Material, type Shape } from './levels';

export interface FloorPlan { /** feet y of the floor */ y: number; /** x range of the floor's standing run */ x0: number; x1: number; }
export interface BuildingPlan { name: string; x: number; y: number; w: number; h: number; floors: [FloorPlan, FloorPlan, FloorPlan]; }
export interface BuildingMapDef { id: string; name: string; build: () => LevelDef; }

const FACILITY: LevelStyle = { sky: ['#1b2433', '#4d5f78'], far: '#2c3a4f', near: '#1f2a3a', water: 'transparent', deep: '#0a0e14', pit: '#0a0e14', horizon: 'moon', art: 'facility' };
const NIGHT: LevelStyle = { sky: ['#070b1a', '#1c2547'], far: '#151b33', near: '#0e1326', water: 'transparent', deep: '#05070d', pit: '#05070d', horizon: 'city', art: 'city' };
const SUNSET: LevelStyle = { sky: ['#3b2347', '#f08a4b'], far: '#6b3d4f', near: '#3d2433', water: 'transparent', deep: '#140b10', pit: '#140b10', horizon: 'city', art: 'dusk' };

/** Wall thickness, slab thickness, door height, landing width by a wall, stair zone width. */
const WT = 30;
const T = 36;
const DOOR = 104;
const LAND = 40;
const SW = 130;

const R = (x: number, y: number, w: number, h: number, m: Material): Shape => ({ kind: 'rect', x, y, w, h, m });

/** A slab at y from x0 to x1 with openings cut out of it. */
function slab(x0: number, x1: number, y: number, cuts: [number, number][], m: Material): Shape[] {
  const out: Shape[] = [];
  let a = x0;
  for (const [c0, c1] of [...cuts].sort((p, q) => p[0] - q[0])) {
    if (c0 > a) out.push(R(a, y, c0 - a, T, m));
    a = Math.max(a, c1);
  }
  if (x1 > a) out.push(R(a, y, x1 - a, T, m));
  return out;
}

/** Stepped blocks low, tall, low under a slab opening: hop up a third of a storey at a time. */
function stair(xs: number, feet: number, s: number, m: Material): Shape[] {
  const h1 = Math.round(s / 3);
  const h2 = Math.round((2 * s) / 3);
  return [R(xs, feet + 1 - h1, 40, h1, m), R(xs + 40, feet + 1 - h2, 50, h2, m), R(xs + 90, feet + 1 - h1, 40, h1, m)];
}

interface TowerSpec { name: string; x0: number; x1: number; g: number; s: number; m: Material; doorsL: boolean; doorsR: boolean; roofStair: boolean; ring: number; }
interface Tower { shapes: Shape[]; plan: BuildingPlan; f: [number, number, number]; roof: number; }

/** A three-storey building: slabs with stair openings at alternating ends, walls with doors, optional ring shaft at the right. */
function tower(sp: TowerSpec): Tower {
  const { x0, x1, g, s, m, ring } = sp;
  const X0 = x0 + WT;
  const X1 = x1 - WT;
  const Xf = X1 - ring;
  const f0 = g - 2 * s;
  const f1 = g - s;
  const rf = g - 3 * s;
  const top = rf + 1;
  const rc: [number, number][] = ring > 0 ? [[Xf, X1]] : [];
  const lg: [number, number] = [X0 + LAND, X0 + LAND + SW];
  const rg: [number, number] = [Xf - LAND - SW, Xf - LAND];
  const out: Shape[] = [
    ...slab(x0, x1, f1 + 1, [rg, ...rc], m),
    ...slab(x0, x1, f0 + 1, [lg, ...rc], m),
    ...slab(x0, x1, top, sp.roofStair ? [rg] : [], m),
    ...stair(rg[0], g, s, m),
    ...stair(lg[0], f1, s, m),
  ];
  if (sp.roofStair) out.push(...stair(rg[0], f0, s, m));
  if (ring > 0) out.push(R(Xf - 12, f0 + 1 - 14, 12, 14, 'metal'));
  const wall = (wx: number, doors: boolean): void => {
    if (!doors) {
      out.push(R(wx, top, WT, g + 1 - top, m));
      return;
    }
    const ys = [top, f0 + 1, f1 + 1];
    const fs = [f0, f1, g];
    for (let i = 0; i < 3; i++) out.push(R(wx, ys[i]!, WT, fs[i]! + 1 - DOOR - ys[i]!, m));
  };
  wall(x0, sp.doorsL);
  wall(X1, sp.doorsR);
  const f0x1 = sp.roofStair ? rg[0] : ring > 0 ? Xf - 12 : X1;
  const plan: BuildingPlan = {
    name: sp.name, x: x0, y: top, w: x1 - x0, h: g + 1 - top,
    floors: [{ y: f0, x0: lg[1], x1: f0x1 }, { y: f1, x0: lg[1], x1: rg[0] }, { y: g, x0: X0, x1: rg[0] }],
  };
  return { shapes: out, plan, f: [f0, f1, g], roof: rf };
}

/** A walkway from feet (xa, ya) to feet (xb, yb): flat or a ramp. */
function bridge(xa: number, ya: number, xb: number, yb: number, m: Material): Shape {
  return { kind: 'poly', pts: [[xa, ya + 1], [xb, yb + 1], [xb, yb + 1 + T], [xa, ya + 1 + T]], m };
}

/** A corridor roof over a walkway, 112 px of headroom. */
function canopy(xa: number, ya: number, xb: number, yb: number, m: Material): Shape {
  return { kind: 'poly', pts: [[xa, ya - 125], [xb, yb - 125], [xb, yb - 111], [xa, ya - 111]], m };
}

/** Links between two buildings on the top and middle floors, optionally roofed, optionally across the roofs. */
function links(a: Tower, b: Tower, m: Material, covered: boolean, roof: boolean): Shape[] {
  const xa = a.plan.x + a.plan.w;
  const xb = b.plan.x;
  const out: Shape[] = [];
  for (const i of [0, 1]) {
    out.push(bridge(xa, a.f[i]!, xb, b.f[i]!, m));
    if (covered) out.push(canopy(xa, a.f[i]!, xb, b.f[i]!, m));
  }
  if (roof) out.push(bridge(xa, a.roof, xb, b.roof, m));
  return out;
}

/** A rooftop tree: hollow trunk door, a planter and a branch to climb into the crown. */
function tree(cx: number, rf: number, dir: 1 | -1): Shape[] {
  const px = dir === 1 ? cx + 80 : cx - 150;
  const bx = dir === 1 ? cx + 30 : cx - 80;
  return [
    R(px, rf + 1 - 50, 70, 50, 'stone'),
    R(cx - 30, rf - 200, 60, 110, 'wood'),
    { kind: 'hole', x: cx, y: rf - 92, r: 24 },
    R(bx, rf - 110, 50, 12, 'wood'),
    { kind: 'circle', x: cx, y: rf - 250, r: 70, m: 'grass' },
    { kind: 'circle', x: cx - 60, y: rf - 225, r: 45, m: 'grass' },
    { kind: 'circle', x: cx + 60, y: rf - 225, r: 45, m: 'grass' },
  ];
}

/** Ground mass: flat rects and ramps along a feet-y polyline. */
function ground(pts: [number, number][], H: number, m: Material): Shape[] {
  const out: Shape[] = [];
  for (let i = 0; i + 1 < pts.length; i++) {
    const [xa, ya] = pts[i]!;
    const [xb, yb] = pts[i + 1]!;
    if (ya === yb) out.push(R(xa, ya + 1, xb - xa, H - ya - 1, m));
    else out.push({ kind: 'poly', pts: [[xa, ya + 1], [xb, yb + 1], [xb, H], [xa, H]], m });
  }
  return out;
}

/** Authored spawn spots snapped onto the floor under them. */
function snapSpawns(level: LevelDef): void {
  const t = buildTerrain(level);
  const snap = (list: [number, number][]): [number, number][] =>
    list.map(([x, y]) => {
      const ground = t.surfaceY(x, Math.max(0, y - 60));
      return [x, ground < t.h ? ground - 1 : y];
    });
  level.spawns = level.spawns.map(snap);
  level.floats = level.floats.map(snap);
}

interface Layout {
  id: string; name: string; W: number; H: number; style: LevelStyle; blurb: string;
  shapes: Shape[]; plans: [BuildingPlan, BuildingPlan, BuildingPlan];
  spawns: [number, number][][]; floats: [number, number][][];
}

const row = (xs: number[], y: number): [number, number][] => xs.map((x) => [x, y]);

/** A wall at wx from top down to the ground floor, with a door at the foot of every floor. */
function doorWall(wx: number, top: number, fs: number[], m: Material): Shape[] {
  const out: Shape[] = [];
  let y = top;
  for (const f of fs) {
    out.push(R(wx, y, WT, f + 1 - DOOR - y, m));
    y = f + 1;
  }
  return out;
}

/**
 * A switchback stairwell by the wall at xw (dir 1: the well runs right of it). Slabs over it have a
 * 90 px opening at the wall; hanging shelves give 70 px hops and leave the floor under them clear.
 */
function well(xw: number, dir: 1 | -1, fs: [number, number, number], m: Material): Shape[] {
  const sh = (a: number, b: number, top: number): Shape => R(dir === 1 ? xw + a : xw - b, top, b - a, 10, m);
  const [, fm, fg] = fs;
  return [sh(0, 60, fg + 1 - 140), sh(70, 130, fg + 1 - 70), sh(0, 60, fm + 1 - 140), sh(90, 150, fm + 1 - 70)];
}

// ---------- Level 1: The Compound (the sketch) ----------
function compound(): Layout {
  const W = 2540;
  const H = 1400;
  const FA: [number, number, number] = [780, 990, 1200];
  const FC: [number, number, number] = [815, 1025, 1235];
  const RA = 571;
  const RC = 606;
  const st: Material = 'stone';
  const mt: Material = 'metal';
  // West tower: narrow, stairwell zig-zagging up its west wall, doors east.
  const A: Shape[] = [
    R(230, RA, WT, 1201 - RA, st),
    ...doorWall(620, RA, FA, st),
    R(230, RA, 420, T, st),
    ...slab(230, 650, 781, [[260, 350]], st),
    ...slab(230, 650, 991, [[260, 350]], st),
    ...well(260, 1, FA, mt),
  ];
  // Hall: two big slabs open at the ends, stepped blocks under the upper slab, shelves up to the roof.
  const B: Shape[] = [
    ...doorWall(810, RA, FA, st),
    ...doorWall(1620, RA, FA, st),
    ...slab(810, 1650, RA, [[1530, 1620]], st),
    ...slab(810, 1650, 781, [[870, 970]], st),
    ...slab(810, 1650, 991, [[1440, 1540]], st),
    R(880, 851, 60, 30, mt), R(970, 921, 70, 70, mt), R(1040, 956, 60, 35, mt),
    R(1380, 1131, 60, 10, mt), R(1460, 1061, 60, 10, mt),
    R(1490, 711, 60, 10, mt), R(1560, 641, 60, 10, mt),
  ];
  // East block: heavy core slabs, stair shaft at its east wall, doors west.
  const C: Shape[] = [
    ...doorWall(1785, RC, FC, mt),
    R(2275, RC, WT, 1236 - RC, mt),
    R(1785, RC, 520, T, mt),
    ...slab(1785, 2305, 816, [[2185, 2275]], mt),
    ...slab(1785, 2305, 1026, [[2185, 2275]], mt),
    R(1815, 852, 370, 24, mt), R(1815, 1062, 370, 24, mt),
    ...well(2275, -1, FC, st),
  ];
  const L: Shape[] = [
    bridge(650, 780, 810, 780, mt), canopy(650, 780, 810, 780, mt),
    bridge(650, 990, 810, 990, mt), canopy(650, 990, 810, 990, mt),
    bridge(1650, 780, 1785, 815, mt), bridge(1650, 990, 1785, 1025, mt),
  ];
  const plans: [BuildingPlan, BuildingPlan, BuildingPlan] = [
    { name: 'West tower', x: 230, y: RA, w: 420, h: 1201 - RA, floors: [{ y: 780, x0: 350, x1: 620 }, { y: 990, x0: 350, x1: 620 }, { y: 1200, x0: 260, x1: 620 }] },
    { name: 'Hall', x: 810, y: RA, w: 840, h: 1201 - RA, floors: [{ y: 780, x0: 970, x1: 1620 }, { y: 990, x0: 1100, x1: 1440 }, { y: 1200, x0: 840, x1: 1620 }] },
    { name: 'East block', x: 1785, y: RC, w: 520, h: 1236 - RC, floors: [{ y: 815, x0: 1815, x1: 2185 }, { y: 1025, x0: 1815, x1: 2185 }, { y: 1235, x0: 1815, x1: 2275 }] },
  ];
  return {
    id: 'bldg:compound', name: 'The Compound', W, H, style: FACILITY,
    blurb: 'A narrow stair tower, a wide slab hall with trees on its roof and a heavy block, three floors each. Corridors, ramps and the courtyard join them.',
    shapes: [
      ...ground([[0, 1200], [1650, 1200], [1785, 1235], [W, 1235]], H, st),
      ...A, ...B, ...C, ...L,
      ...tree(1010, 570, 1), ...tree(1440, 570, -1),
    ],
    plans,
    spawns: [row([600, 550, 500, 450], 1200), row([1850, 1900, 1950, 2000], 1235), [[1150, 990], [1300, 990], [1220, 1200], [1250, 780]]],
    floats: [row([480, 560], 990), row([1900, 2000], 1025), row([1300], 780)],
  };
}

// ---------- Level 2: Sky Bridges (symmetric towers) ----------
function skybridges(): Layout {
  const W = 2600;
  const H = 1400;
  const A = tower({ name: 'West spire', x0: 60, x1: 740, g: 1180, s: 180, m: 'metal', doorsL: false, doorsR: true, roofStair: true, ring: 0 });
  const B = tower({ name: 'Core tower', x0: 860, x1: 1740, g: 1180, s: 180, m: 'stone', doorsL: true, doorsR: true, roofStair: true, ring: 64 });
  const C = tower({ name: 'East spire', x0: 1860, x1: 2540, g: 1180, s: 180, m: 'metal', doorsL: true, doorsR: false, roofStair: true, ring: 0 });
  return {
    id: 'bldg:skybridges', name: 'Sky Bridges', W, H, style: NIGHT,
    blurb: 'Three towers of equal height at night, joined by bridges on every floor and across the roofs. A fair fight from top to bottom.',
    shapes: [
      ...ground([[0, 1180], [W, 1180]], H, 'rock'),
      ...A.shapes, ...B.shapes, ...C.shapes,
      ...links(A, B, 'metal', false, true), ...links(B, C, 'metal', false, true),
      ...tree(1060, B.roof, 1), ...tree(1380, B.roof, -1),
    ],
    plans: [A.plan, B.plan, C.plan],
    spawns: [row([520, 460, 400, 340], 1000), row([2090, 2150, 2210, 2270], 1000), [[1100, 1000], [1420, 1000], [1260, 1000], [1260, 1180]]],
    floats: [row([300, 400], 1180), row([2200, 2300], 1180), row([1260], 820)],
  };
}

// ---------- Level 3: Stepped Skyline (low, tall, medium) ----------
function skyline(): Layout {
  const W = 2700;
  const H = 1450;
  const A = tower({ name: 'Low house', x0: 60, x1: 740, g: 1240, s: 160, m: 'wood', doorsL: false, doorsR: true, roofStair: true, ring: 0 });
  const B = tower({ name: 'High rise', x0: 900, x1: 1640, g: 1200, s: 200, m: 'stone', doorsL: true, doorsR: true, roofStair: false, ring: 64 });
  const C = tower({ name: 'Mid hall', x0: 1800, x1: 2640, g: 1220, s: 170, m: 'stone', doorsL: true, doorsR: false, roofStair: true, ring: 0 });
  return {
    id: 'bldg:skyline', name: 'Stepped Skyline', W, H, style: SUNSET,
    blurb: 'A low house, a high rise and a hall at dusk, joined by ramps up the facades. Climb to the rooftop trees for the view.',
    shapes: [
      ...ground([[0, 1240], [740, 1240], [900, 1200], [1640, 1200], [1800, 1220], [W, 1220]], H, 'rock'),
      ...A.shapes, ...B.shapes, ...C.shapes,
      ...links(A, B, 'wood', false, false), ...links(B, C, 'metal', true, false),
      ...tree(300, A.roof, 1), ...tree(2200, C.roof, -1),
    ],
    plans: [A.plan, B.plan, C.plan],
    spawns: [row([520, 460, 400, 340], 1080), row([2030, 2090, 2150, 2210], 1050), [[1130, 1000], [1340, 1000], [1240, 1000], [1240, 1200]]],
    floats: [row([300, 400], 1240), row([2200, 2300], 1220), row([1240], 800)],
  };
}

const LAYOUTS: readonly Layout[] = [compound(), skybridges(), skyline()];

function toLevel(l: Layout): LevelDef {
  const level: LevelDef = {
    id: l.id as LevelDef['id'], name: l.name, width: l.W, height: l.H, waterY: null, gravity: 1, stamina: 1.3, jump: 1,
    style: l.style, blurb: l.blurb, shapes: l.shapes, boats: [],
    spawns: l.spawns.map((s) => s.map(([x, y]) => [x, y] as [number, number])),
    floats: l.floats.map((s) => s.map(([x, y]) => [x, y] as [number, number])),
  };
  snapSpawns(level);
  return level;
}

export const BUILDING_MAPS: readonly BuildingMapDef[] = LAYOUTS.map((l) => ({ id: l.id, name: l.name, build: () => toLevel(l) }));

export const BUILDING_MAP_IDS: readonly string[] = BUILDING_MAPS.map((m) => m.id);

export const BUILDING_PLANS: Record<string, readonly [BuildingPlan, BuildingPlan, BuildingPlan]> = Object.fromEntries(
  LAYOUTS.map((l) => [l.id, l.plans] as const),
);

const BY_ID = new Map(BUILDING_MAPS.map((m) => [m.id, m]));

export function isBuildingMap(id: string): boolean {
  return BY_ID.has(id);
}

const cache = new Map<string, LevelDef>();

/** The level for a building map id, built once per session. */
export function buildingLevel(id: string): LevelDef | null {
  const def = BY_ID.get(id);
  if (!def) return null;
  const hit = cache.get(id);
  if (hit) return hit;
  const level = def.build();
  cache.set(id, level);
  return level;
}

export function buildingTags(): string[] {
  return ['land', 'buildings'];
}
